import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Upload, Space, Alert, App } from 'antd';
import { UserOutlined, LockOutlined, UploadOutlined, BankOutlined, PhoneOutlined, IdcardOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabase';

const { Title } = Typography;

const RegisterPage = () => {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { message } = App.useApp(); // Use the message from context

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG files!');
      return Upload.LIST_IGNORE;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return Upload.LIST_IGNORE;
    }
    setImageFile(file);
    return false;
  };

  const handleChange = (info) => {
    if (info.file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageUrl(e.target.result);
      };
      reader.readAsDataURL(info.file);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    setError('');

    try {
      // 1. Create user
      const { data: { user }, error: signUpError } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            first_name: values.firstName,
            last_name: values.lastName,
            phone: values.phone
          }
        }
      });

      if (signUpError) throw signUpError;

      if (!user) {
        throw new Error('Signup failed');
      }

      // 2. Upload logo if exists
      let logoUrl = null;
      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('company-logos')
          .upload(fileName, imageFile);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('company-logos')
          .getPublicUrl(fileName);

        logoUrl = urlData.publicUrl;
      }

      // 3. Create company record
      const { error: companyError } = await supabase
        .from('companies')
        .insert([
          {
            user_id: user.id,
            name: values.companyName,
            logo_url: logoUrl,
            business_type: values.businessType,
            registration_number: values.registrationNumber,
            contact_email: values.email,
            contact_phone: values.phone,
          }
        ]);

      if (companyError) throw companyError;

      message.success('Registration successful! Please check your email for verification link.');
      navigate('/login');
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.message);
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <div className="text-center mb-8">
          <Title level={2}>Create Account</Title>
          <p className="text-gray-600">Register your company</p>
        </div>

        {error && (
          <Alert
            message="Registration Error"
            description={error}
            type="error"
            showIcon
            className="mb-4"
          />
        )}

        <Form
          form={form}
          name="register"
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          <div className="bg-gray-50 p-4 rounded mb-4">
            <Title level={4}>Personal Information</Title>
            <Form.Item
              name="firstName"
              rules={[{ required: true, message: 'Please input your first name!' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="First Name" />
            </Form.Item>

            <Form.Item
              name="lastName"
              rules={[{ required: true, message: 'Please input your last name!' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Last Name" />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Email address" />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please input your password!' },
                { min: 6, message: 'Password must be at least 6 characters!' }
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Password" />
            </Form.Item>

            <Form.Item
              name="phone"
              rules={[{ required: true, message: 'Please input your phone number!' }]}
            >
              <Input prefix={<PhoneOutlined />} placeholder="Phone Number" />
            </Form.Item>
          </div>

          <div className="bg-gray-50 p-4 rounded">
            <Title level={4}>Company Information</Title>
            <Form.Item
              name="companyName"
              rules={[{ required: true, message: 'Please input company name!' }]}
            >
              <Input prefix={<BankOutlined />} placeholder="Company Name" />
            </Form.Item>

            <Form.Item
              name="businessType"
              rules={[{ required: true, message: 'Please input business type!' }]}
            >
              <Input prefix={<BankOutlined />} placeholder="Business Type" />
            </Form.Item>

            <Form.Item
              name="registrationNumber"
              rules={[{ required: true, message: 'Please input registration number!' }]}
            >
              <Input prefix={<IdcardOutlined />} placeholder="Registration Number" />
            </Form.Item>

            <Form.Item label="Company Logo">
              <Upload
                listType="picture-card"
                showUploadList={false}
                beforeUpload={beforeUpload}
                onChange={handleChange}
                maxCount={1}
              >
                {imageUrl ? (
                  <img src={imageUrl} alt="Logo" style={{ width: '100%' }} />
                ) : (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </div>

          <Form.Item className="mt-4">
            <Button type="primary" htmlType="submit" loading={loading} block>
              Register
            </Button>
          </Form.Item>

          <div className="text-center">
            <Button type="link" href="/login">
              Already have an account? Login
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default RegisterPage;